/**
 * KIOSK LOOKUP, AND WHAT THE DEVICE IS ALLOWED TO REMEMBER.
 * =======================================================
 *
 * ⚠️ **KIOSK PAYLOADS ARE NEVER WRITTEN TO DISK. THAT IS A CONTROL, NOT AN OVERSIGHT.**
 * `src/core/api.ts` and `vite.config.ts` both state the rule and the reason: every API response
 * carries `Cache-Control: no-store`, the CloudFront `api/*` behaviour is `CACHING_DISABLED`, and the
 * service worker refuses to cache `/api/*` — because *"a cached kiosk payload is another supplier's
 * data served without an authorization check (C-04)"*. Putting the same payload in IndexedDB or
 * `localStorage` would reintroduce exactly that: a durable copy of one supplier's kiosk record,
 * readable by whoever next uses the device or the browser profile, with no ownership check in front
 * of it.
 *
 * So the only memory here is {@link recallKiosk}: a **module-level `Map`, in memory, for the lifetime
 * of this page**. It dies with the tab, it is never serialised, and it holds only what this session
 * already fetched and displayed. Its single purpose is that a technician who looked a kiosk up on the
 * way in does not lose those details when the van drives into a dead spot.
 *
 * The consequence is stated plainly to the technician rather than hidden: **offline, a KID this
 * session has not already fetched cannot be looked up at all.** {@link LookupResult}'s
 * `unseen-offline` variant exists so the UI says that in those words, instead of showing a spinner or
 * an error that reads like a fault. Work can still start — the KID is recorded and the kiosk details
 * are simply blank, which is honest and is not a blocker.
 */

import { getKiosk, type ApiOutcome, type KioskPayload, type RequestOptions } from '@/core/api';
import type { PrefillInputs } from '@/forms';
import type { KioskContext } from './flow';

/* -------------------------------------------------------------- kiosk ids ---- */

/**
 * Normalise what a gloved thumb produced into what the server will accept.
 *
 * Spaces and dashes are stripped because a KID read off a dispatch sheet is often written in groups.
 * The upper-casing is retained as a harmless no-op: real KIDs are all digits (see
 * {@link isWellFormedKid}), so it changes nothing, and keeping it means a stray letter is preserved
 * verbatim for the "not a valid KID" message rather than half-normalised. Nothing else is changed —
 * in particular no characters are substituted, because "helpfully" turning an `O` into a `0` would
 * look up a different kiosk.
 */
export function normaliseKid(raw: string): string {
  return raw.replace(/[\s-]+/g, '').toUpperCase();
}

/**
 * The server's own pattern (`infra/src/handlers/kiosk-read.ts`), checked locally so a typo costs no
 * round trip. Client and server MUST agree on this rule.
 *
 * Digits only, 6–10 of them. A live census of `Intervention__c.Kiosk_ID__c` (2026-09-04, 148,745
 * non-null rows) found every value all-numeric at lengths 6–9; the Salesforce `Asset.Kiosk_ID__c`
 * field is `string(10)`, so 10 is allowed as declared headroom. Letters never occur. The previous
 * `[A-Za-z0-9]{8}` rule rejected ~10% of real kiosks (the 6–7 digit ones).
 */
export function isWellFormedKid(kid: string): boolean {
  return /^[0-9]{6,10}$/.test(kid);
}

/* ------------------------------------------------------- in-memory recall ---- */

const seenThisSession = new Map<string, KioskPayload>();

export function rememberKiosk(payload: KioskPayload): void {
  seenThisSession.set(normaliseKid(payload.kioskId), payload);
}

export function recallKiosk(kioskId: string): KioskPayload | undefined {
  return seenThisSession.get(normaliseKid(kioskId));
}

/** Test hook, and what a sign-out would call if sign-in existed. */
export function forgetKiosks(): void {
  seenThisSession.clear();
}

/* ------------------------------------------------------------- the lookup ---- */

export type LookupResult =
  | { readonly kind: 'found'; readonly payload: KioskPayload; readonly from: 'server' | 'session' }
  /**
   * Offline, and this session has never fetched this KID. Distinct from every failure variant because
   * it is not a failure: there is simply nothing on the device to show, by design.
   */
  | { readonly kind: 'unseen-offline'; readonly kioskId: string }
  /** Anything the API layer reported. Rendered by `ApiNotice`, which keeps the variants apart. */
  | { readonly kind: 'problem'; readonly outcome: ApiOutcome<KioskPayload> };

/**
 * Look a kiosk up.
 *
 * The order matters. The request is attempted first, and the in-memory copy is consulted only after a
 * connectivity-shaped outcome — so a stale in-memory payload can never shadow a fresh one. That is the
 * opposite of a cache-first strategy, deliberately: this app's whole problem is telling the technician
 * what is true, and a cache-first read would sometimes answer with yesterday's kiosk status without
 * saying so.
 */
export async function lookupKiosk(rawKid: string, options: RequestOptions = {}): Promise<LookupResult> {
  const kioskId = normaliseKid(rawKid);
  const outcome = await getKiosk(kioskId, {}, options);

  if (outcome.kind === 'ok') {
    rememberKiosk(outcome.data);
    return { kind: 'found', payload: outcome.data, from: 'server' };
  }

  if (outcome.kind === 'offline' || outcome.kind === 'network-error') {
    const remembered = recallKiosk(kioskId);
    if (remembered !== undefined) {
      return { kind: 'found', payload: remembered, from: 'session' };
    }
    if (outcome.kind === 'offline') {
      return { kind: 'unseen-offline', kioskId };
    }
  }

  return { kind: 'problem', outcome };
}

/** Build the flow's kiosk context from a lookup that succeeded. */
export function contextFromLookup(result: Extract<LookupResult, { kind: 'found' }>): KioskContext {
  return {
    kioskId: result.payload.kioskId,
    payload: result.payload,
    source: result.from,
  };
}

/** A KID the technician typed that we could not resolve. Recorded, but claimed as nothing more. */
export function contextFromTypedKid(kioskId: string): KioskContext {
  return { kioskId: normaliseKid(kioskId), payload: null, source: 'typed-only' };
}

/* ------------------------------------------------------------ the prefill ---- */

/**
 * `KioskPayload` → prefill catalog keys.
 *
 * Every line below was checked against the server's reducer, `infra/src/domain/kiosk-payload.ts`
 * (read 2026-09-03), which is the file that decides which of the 355 fields survive into the reduced
 * C-15 payload and under what names. The catalog keys are from `src/forms/prefill.ts`.
 *
 * **THREE CATALOG KEYS ARE DELIBERATELY ABSENT, and each absence is a fact about the payload rather
 * than a gap in this function:**
 *
 *  · `kiosk.moduleCount` — derived from `Hub_Size__c`, which the reducer does not emit. It is left
 *    unset so `deriveContext` falls back to the layout string's length, which is its documented
 *    fallback. Passing a made-up module count would gate 20 serial questions on a guess.
 *  · `module.serials` — `SerializedProduct` rows are not in the reduced payload at all, so the
 *    untrusted serial *reference* the schemas can show simply has no source here. The serial
 *    questions are asked with nothing beside them, which is correct: no reference is better than a
 *    fabricated one.
 *  · `intervention.*` — these come from `GET /interventions`, not from a kiosk read. That route
 *    answers `503 PRD-0.12-salesforce-identity` today, so there is no intervention to read.
 *
 * `kiosk.totalSlots` IS mapped, and it is marked `trusted: false` in the catalog — so `createState`
 * puts it in `reference` and never adopts it as an answer. The reducer already refuses to emit a `0`
 * (Trap 5: verified reading 0 on a Gen-4 kiosk with 23 real slots), and the catalog refuses to trust
 * what does get through. Two independent guards, both intentional.
 */
export function prefillInputsFromKiosk(payload: KioskPayload | null): PrefillInputs {
  if (payload === null) return {};

  // Optional chaining rather than `?? {}` locals: the nested blocks are all optional AND
  // `exactOptionalPropertyTypes` is on, so a `{}` fallback widens the type to a union that cannot be
  // indexed. Chaining keeps every access typed as `string | number | undefined`.
  const inputs: Record<string, string | number | boolean | null> = {};
  const put = (key: string, value: string | number | undefined): void => {
    if (value === undefined) return;
    if (typeof value === 'string' && value.trim() === '') return;
    inputs[key] = value;
  };

  put('kiosk.id', payload.kioskId);
  put('kiosk.name', payload.name);
  put('kiosk.status', payload.status?.state);

  put('kiosk.generation', payload.hardware?.['generation']);
  put('kiosk.color', payload.hardware?.['color']);
  put('kiosk.accessPointType', payload.hardware?.['accessPointType']);
  put('kiosk.mountType', payload.hardware?.['mountType']);
  put('kiosk.placement', payload.hardware?.['environment']);
  put('kiosk.layout', payload.hardware?.['layoutString']);
  put('kiosk.totalSlots', payload.hardware?.['totalSlots']);
  put('kiosk.macAddress', payload.hardware?.['macAddress']);

  put('kiosk.siteName', payload.location?.['siteName']);
  put('kiosk.street', payload.location?.['street']);
  put('kiosk.countryCode', payload.location?.['countryCode']);

  put('kiosk.directions', payload.access?.['friendlyDirections']);
  put('kiosk.accessMechanism', payload.access?.['mechanism']);
  put('kiosk.floorType', payload.access?.['floorType']);
  put('case.parkingRestrictions', payload.access?.['parkingRestrictions']);
  put('case.riskLevel', payload.access?.['riskLevel']);

  put('kiosk.contactEmail', payload.siteContact?.email);
  put('kiosk.contactMobile', payload.siteContact?.mobile);

  put('case.number', payload.caseNumber);
  put('case.installationMethod', payload.prefill?.installMethod);

  return inputs;
}
